/**
 * Plain-English labels, colors, and small formatters shared across the Service
 * Desk pages. Statuses and priorities are fixed by the data model
 * (docs/servicedesk.md); everything else on the desk is configured in the
 * database and comes from the api.
 */

import type { SdPriority, SdStatus } from '@ampersandbrands/ui';
import type { SdSlaState, SdStaffTicketRow, SdTicketEvent } from '@/lib/serviceDeskTypes';

export const STATUS_LABELS: Record<SdStatus, string> = {
  new: 'New',
  waiting_support: 'Waiting on IT',
  in_progress: 'In progress',
  waiting_customer: 'Waiting on requester',
  waiting_vendor: 'Waiting on vendor',
  on_hold: 'On hold',
  pending_hr_approval: 'Pending HR approval',
  resolved: 'Resolved',
  closed: 'Closed',
};

export const ALL_STATUSES: SdStatus[] = [
  'new',
  'waiting_support',
  'in_progress',
  'waiting_customer',
  'waiting_vendor',
  'on_hold',
  'pending_hr_approval',
  'resolved',
  'closed',
];

/** The statuses that count as still open (the queue default). */
export const OPEN_STATUSES: SdStatus[] = [
  'new',
  'waiting_support',
  'in_progress',
  'waiting_customer',
  'waiting_vendor',
  'on_hold',
  'pending_hr_approval',
];

export const PRIORITY_LABELS: Record<SdPriority, string> = {
  critical: 'Critical',
  high: 'High',
  normal: 'Normal',
  low: 'Low',
};

export const ALL_PRIORITIES: SdPriority[] = ['critical', 'high', 'normal', 'low'];

export const STATUS_TONES: Record<SdStatus, string> = {
  new: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  waiting_support: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200',
  in_progress: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  waiting_customer: 'bg-slate-100 text-slate-700 dark:bg-slate-800/60 dark:text-slate-300',
  waiting_vendor: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300',
  on_hold: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300',
  pending_hr_approval: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300',
  resolved: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  closed: 'bg-slate-100 text-slate-600 dark:bg-slate-800/60 dark:text-slate-400',
};

export const PRIORITY_TONES: Record<SdPriority, string> = {
  critical: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
  normal: 'bg-slate-100 text-slate-700 dark:bg-slate-800/60 dark:text-slate-300',
  low: 'bg-slate-50 text-slate-500 dark:bg-slate-800/40 dark:text-slate-400',
};

export const SLA_TONES: Record<SdSlaState, string> = {
  ok: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  warning: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200',
  breached: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
  paused: 'bg-slate-100 text-slate-700 dark:bg-slate-800/60 dark:text-slate-300',
  done: 'bg-slate-100 text-slate-600 dark:bg-slate-800/60 dark:text-slate-400',
};

export const SLA_STATE_LABELS: Record<SdSlaState, string> = {
  ok: 'On track',
  warning: 'Due soon',
  breached: 'Late',
  paused: 'Paused',
  done: 'Done',
};

/** "2h 15m", "40m", "3d 4h". Input in milliseconds, always positive. */
export function formatSpan(ms: number): string {
  const mins = Math.max(0, Math.round(ms / 60_000));
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) {
    const m = mins % 60;
    return m ? `${hours}h ${m}m` : `${hours}h`;
  }
  const days = Math.floor(hours / 24);
  const h = hours % 24;
  return h ? `${days}d ${h}h` : `${days}d`;
}

/**
 * The SLA chip text for a queue row. Uses the earlier of the two deadlines the
 * api reports, because the one that bites first is the one IT needs to see.
 */
export function slaChipText(
  row: Pick<SdStaffTicketRow, 'sla_state' | 'first_response_due_at' | 'resolve_due_at'>,
  now = Date.now(),
): string {
  const base = SLA_STATE_LABELS[row.sla_state];
  if (row.sla_state === 'paused' || row.sla_state === 'done') return base;
  const dues = [row.first_response_due_at, row.resolve_due_at]
    .filter((d): d is string => !!d)
    .map((d) => new Date(d).getTime())
    .filter((t) => !Number.isNaN(t));
  if (dues.length === 0) return base;
  const due = Math.min(...dues);
  const delta = due - now;
  return delta >= 0 ? `${base}, ${formatSpan(delta)} left` : `${base}, ${formatSpan(-delta)} over`;
}

export const KIND_LABELS: Record<string, string> = {
  problem: 'Problem',
  request: 'Request',
  question: 'Question',
};

export const ARTICLE_TYPE_LABELS: Record<string, string> = {
  howto: 'How-to',
  troubleshooting: 'Troubleshooting',
  policy: 'Policy',
  runbook: 'Runbook',
};

export const ARTICLE_STATUS_TONES: Record<string, string> = {
  draft: 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200',
  published: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  retired: 'bg-slate-100 text-slate-600 dark:bg-slate-800/60 dark:text-slate-400',
};

export const ARTICLE_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  published: 'Published',
  retired: 'Retired',
};

export const AUDIENCE_LABELS: Record<string, string> = {
  stores: 'Stores',
  corp: 'Corporate',
  it: 'IT only',
};

/** Audiences shown as checkboxes in the editors. */
export const EDITABLE_AUDIENCES = ['stores', 'corp', 'it'] as const;

export const EVENT_LABELS: Record<string, string> = {
  created: 'opened the ticket',
  status_changed: 'changed the status',
  assigned: 'changed the assignee',
  priority_changed: 'changed the priority',
  category_changed: 'changed the category',
  moved_desk: 'moved the ticket to another desk',
  confirmed_bug: 'confirmed this as a bug',
  topic_changed: 'changed the topics',
  title_changed: 'changed the title',
  vendor_ticket_set: 'set the vendor ticket number',
  hr_restricted_changed: 'changed the HR restriction',
  sla_warning: 'SLA warning sent',
  sla_breached: 'SLA deadline passed',
  automation_run: 'ran an automation',
  hr_approval_requested: 'asked HR to approve this',
  hr_approved: 'approved this for HR',
  hr_declined: 'declined this for HR',
  hr_approval_reminded: 'reminded the HR approvers',
  offboarding_ready: 'marked this ready to offboard',
};

const WORDS: Record<string, string> = { ...STATUS_LABELS, ...PRIORITY_LABELS };
const word = (v: unknown): string => (typeof v === 'string' ? (WORDS[v] ?? v) : '');

/** One line describing a system event, for the ticket timeline. */
export function eventText(e: SdTicketEvent): string {
  const base = EVENT_LABELS[e.event_type] ?? e.event_type.replaceAll('_', ' ');
  const d = e.data ?? {};
  const from = word(d['from']);
  const to = word(d['to']);
  if (from && to) return `${base} from ${from} to ${to}`;
  if (to) return `${base} to ${to}`;
  return base;
}
